// Which company to sign in to (04-sign-in.md, "The address and the company", "The email step";
// 94-services-api.md, "Sign-in discovery"). Only the part after the "@" ever leaves the browser.
import { entryFor, type CompanyEntry, type Config } from '../core/config';
import { readJson, writeJson } from '../core/storage';

/** A company entry discovery answered, its paths resolved against its `app` address. */
type Found = Record<string, CompanyEntry & { app: string }>;

const FOUND = 'desyr_companies_found';
const LAST_EMAIL = 'desyr_last_email';
const CHOSEN = 'desyr_sign_in_company';

// Each is also kept in memory, so the page works with storage blocked (04, Data).
const memory: { found?: Found; email?: string; chosen?: string } = {};

export const found = (): Found => (memory.found ??= readJson<Found>(FOUND) ?? {});

/** The company entry of a chat server: config.json's wins over a found one. */
export const companyEntry = (config: Config, server: string) => entryFor(config.desyrSignIn, server) ?? entryFor(found(), server);

/** A server named in config.json (listed or with an entry) or found on this device before. */
export const isKnownServer = (config: Config, server: string) =>
  !!entryFor(Object.fromEntries((config.homeserverList ?? []).map((s) => [s, true])), server) || !!companyEntry(config, server);

/** config.json's company: its default server, else its only desyrSignIn entry. */
export function defaultServer(config: Config): string | undefined {
  const listed = config.homeserverList?.[config.defaultHomeserver ?? 0] ?? config.homeserverList?.[0];
  const entries = Object.keys(config.desyrSignIn ?? {});
  return listed ?? (entries.length === 1 ? entries[0] : undefined);
}

export const discoveryOn = (config: Config) => config.desyrDiscover !== '';

/** Nothing to sign in to: no company server, and discovery off (04, "Not set up yet"). */
export const notSetUp = (config: Config) => !defaultServer(config) && !discoveryOn(config);

export const lastEmail = () => (memory.email ??= readJson<string>(LAST_EMAIL) ?? '');
export function rememberEmail(email: string): void {
  memory.email = email;
  writeJson(LAST_EMAIL, email);
}
export const forgetEmail = () => ((memory.email = ''), writeJson(LAST_EMAIL, undefined));

export function chosenCompany(): string | undefined {
  if (memory.chosen !== undefined) return memory.chosen || undefined;
  try {
    return sessionStorage.getItem(CHOSEN) ?? undefined;
  } catch {
    return undefined;
  }
}
export function chooseCompany(server: string | undefined): void {
  memory.chosen = server ?? '';
  try {
    if (server) sessionStorage.setItem(CHOSEN, server);
    else sessionStorage.removeItem(CHOSEN);
  } catch {
    // storage refused: memory only
  }
}

const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** The domain of a work address in its ASCII form, or undefined when it isn't an address. */
export function domainOf(email: string): string | undefined {
  const parts = email.trim().split('@');
  if (parts.length !== 2 || !parts[0]) return undefined;
  let domain = parts[1].trim().replace(/\.$/, '').toLowerCase();
  if (!domain || /[\s/:?#\\]/.test(domain)) return undefined;
  try {
    domain = new URL(`http://${domain}`).hostname; // punycode for an international name
  } catch {
    return undefined;
  }
  const labels = domain.split('.');
  return domain.length <= 253 && labels.length >= 2 && labels.every((l) => LABEL.test(l)) ? domain : undefined;
}

export type Discovery =
  | { kind: 'found'; server: string }
  | { kind: 'unknown' }
  | { kind: 'bad' }
  | { kind: 'busy'; minutes: number }
  | { kind: 'down' }
  | { kind: 'none' };

/** GET /_desyr/discover?domain=…: no cookies, not from the cache, a 15 s limit. */
export async function discover(config: Config, domain: string): Promise<Discovery> {
  const base = config.desyrDiscover || '/_desyr/discover';
  let answer: Response;
  try {
    answer = await fetch(`${base}?domain=${encodeURIComponent(domain)}`, {
      credentials: 'omit',
      cache: 'no-store',
      signal: AbortSignal.timeout(15_000),
    });
  } catch {
    return { kind: 'none' };
  }
  const body = await answer.json().catch(() => undefined);
  const code: unknown = body?.code;
  if (answer.status === 200) {
    const { server_name, mas, password, app } = body ?? {};
    if (typeof server_name !== 'string' || typeof mas !== 'string' || typeof password !== 'string' || !/^https?:\/\//.test(app ?? ''))
      return { kind: 'none' };
    const resolve = (p: unknown) => (typeof p === 'string' ? new URL(p, app).href : undefined);
    const entry = { app, mas, password: resolve(password), mail: resolve(body.mail), admin: resolve(body.admin), push: resolve(body.push) };
    memory.found = { ...found(), [server_name]: entry };
    writeJson(FOUND, memory.found);
    return { kind: 'found', server: server_name };
  }
  if (answer.status === 404 && code === 'desyr.discover.not_found') return { kind: 'unknown' };
  if (answer.status === 400 && code === 'desyr.discover.bad_domain') return { kind: 'bad' };
  if (answer.status === 429 && code === 'desyr.discover.rate_limited') {
    const seconds = Number(answer.headers.get('Retry-After'));
    return { kind: 'busy', minutes: seconds > 0 ? Math.ceil(seconds / 60) : 1 };
  }
  if (answer.status >= 500 && typeof code === 'string' && code.startsWith('desyr.')) return { kind: 'down' };
  return { kind: 'none' }; // no discovery here: config.json's company
}
